import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Input, Button, message, Table, Modal, Select, Space, Card } from 'antd';
import CreatePostMap from '../components/CreatePostMap';
import { API_BASE_URL } from '../config/api';
import { get, post, isAuthenticated } from '../utils/api';
import HelpPostMap from '../components/HelpPostMap';
import './css/HelpPost.css';
import { useNavigate } from 'react-router-dom';
import { EnvironmentOutlined, AimOutlined, InfoCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import PostAddIcon from '@mui/icons-material/PostAdd';

const { Option } = Select;

const HelpPost = () => {
  const [form] = Form.useForm();
  const [posts, setPosts] = useState([]);
  const [position, setPosition] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredPostId, setHoveredPostId] = useState(null);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [regionsData, setRegionsData] = useState([]);
  const [tempSelectedProvince, setTempSelectedProvince] = useState(null);
  const [tempSelectedCity, setTempSelectedCity] = useState(null);
  const [tempSelectedDistrict, setTempSelectedDistrict] = useState(null);
  
  const isUserLoggedIn = isAuthenticated();
  const mapRef = useRef(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
    fetchRegionsWithLocation();
    // Only fetch cats if user is logged in
    if (isUserLoggedIn) {
      fetchCats();
    }
  }, [isUserLoggedIn]);
  
  // Initialize temp values with selected values
  useEffect(() => {
    setTempSelectedProvince(selectedProvince);
  }, [selectedProvince]);
  
  useEffect(() => {
    setTempSelectedCity(selectedCity);
  }, [selectedCity]);
  
  useEffect(() => {
    setTempSelectedDistrict(selectedDistrict);
  }, [selectedDistrict]);

  // Fetch regions with location data
  const fetchRegionsWithLocation = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/get_regions_with_location/`);
      setRegionsData(response.data);
      
      // Extract unique provinces
      const uniqueProvinces = [...new Set(response.data
        .filter(item => item.province)
        .map(item => item.province))]
        .map(province => ({ value: province, label: province }));
      
      setProvinces(uniqueProvinces);
      
      // Set default province if available
      if (uniqueProvinces.length > 0) {
        setSelectedProvince(uniqueProvinces[0].value);
      }
    } catch (error) {
      console.error('Failed to fetch regions with location:', error);
      message.error('Failed to fetch regions data');
    }
  };

  // Update cities when province changes
  useEffect(() => {
    if (selectedProvince && regionsData.length > 0) {
      const filteredCities = regionsData
        .filter(item => item.province === selectedProvince && item.city)
        .map(item => ({
          value: item.city,
          label: item.city,
          coordinates: { latitude: item.lat, longitude: item.lng }
        }));
      
      // Remove duplicates (some cities appear multiple times with different districts)
      const uniqueCities = Array.from(new Map(filteredCities.map(city => 
        [city.value, city])).values());
      
      setCities(uniqueCities);
      
      // Reset selected city and district
      setSelectedCity(null);
      setSelectedDistrict(null);
      setDistricts([]);
    }
  }, [selectedProvince, regionsData]);
  
  // Update cities when temp province changes
  useEffect(() => {
    if (tempSelectedProvince && regionsData.length > 0) {
      const filteredCities = regionsData
        .filter(item => item.province === tempSelectedProvince && item.city)
        .map(item => ({
          value: item.city,
          label: item.city,
          coordinates: { latitude: item.lat, longitude: item.lng }
        }));
      
      // Remove duplicates (some cities appear multiple times with different districts)
      const uniqueCities = Array.from(new Map(filteredCities.map(city => 
        [city.value, city])).values());
      
      setCities(uniqueCities);
    }
  }, [tempSelectedProvince, regionsData]);

  // Update districts when city changes
  useEffect(() => {
    if (selectedProvince && selectedCity && regionsData.length > 0) {
      const filteredDistricts = regionsData
        .filter(item => 
          item.province === selectedProvince && 
          item.city === selectedCity && 
          item.district)
        .map(item => ({
          value: item.district,
          label: item.district,
          coordinates: { latitude: item.lat, longitude: item.lng }
        }));
      
      setDistricts(filteredDistricts);
      
      // Reset selected district
      setSelectedDistrict(null);
    }
  }, [selectedProvince, selectedCity, regionsData]);
  
  // Update districts when temp city changes
  useEffect(() => {
    if (tempSelectedProvince && tempSelectedCity && regionsData.length > 0) {
      const filteredDistricts = regionsData
        .filter(item => 
          item.province === tempSelectedProvince && 
          item.city === tempSelectedCity && 
          item.district)
        .map(item => ({
          value: item.district,
          label: item.district,
          coordinates: { latitude: item.lat, longitude: item.lng }
        }));
      
      setDistricts(filteredDistricts);
    }
  }, [tempSelectedProvince, tempSelectedCity, regionsData]);

  const fetchPosts = async () => {
    try {
      // Don't require authentication for viewing posts
      const data = await get('/api/get-posts/');
      setPosts(data);
      setLoading(false);
    } catch (error) {
      message.error('Failed to fetch posts');
      setLoading(false);
    }
  };

  const fetchCats = async () => {
    try {
      const data = await get('/api/my-cats/');
      setCats(data);
    } catch (error) {
      message.error('Failed to fetch cats');
    }
  };

  // Reference to the CreatePostMap component
  const createPostMapRef = useRef(null);

  // Handlers for province, city, and district selection in the modal
  const handleProvinceSelectInModal = (province) => {
    setSelectedProvince(province);
    form.setFieldsValue({ city: undefined, district: undefined });
  };

  const handleCitySelectInModal = (city) => {
    setSelectedCity(city);
    form.setFieldsValue({ district: undefined });
    
    // Center the map in the modal on the selected city
    const selectedCityData = cities.find(c => c.value === city);
    if (selectedCityData && selectedCityData.coordinates && createPostMapRef.current) {
      createPostMapRef.current.centerMap(
        selectedCityData.coordinates.latitude, 
        selectedCityData.coordinates.longitude
      );
    }
  };

  const handleDistrictSelectInModal = (district) => {
    setSelectedDistrict(district);
    
    // Center the map in the modal on the selected district
    const selectedDistrictData = districts.find(d => d.value === district);
    if (selectedDistrictData && selectedDistrictData.coordinates && createPostMapRef.current) {
      createPostMapRef.current.centerMap(
        selectedDistrictData.coordinates.latitude, 
        selectedDistrictData.coordinates.longitude
      );
    }
  };

  const onFinish = async (values) => {
    const postData = {
      title: values.title,
      description: values.description,
      latitude: position ? position.latitude : null,
      longitude: position ? position.longitude : null,
      province: values.province,
      city: values.city,
      district: values.district,
      cat: values.cat,
      post_type: values.post_type,
    };

    try {
      const response = await post('/api/help-posts/', postData);
      message.success('互助贴已发布!');
      form.resetFields();
      setPosition(null);
      fetchPosts(); // Refresh the list of posts
      setIsModalVisible(false); // Close the modal after successful submission
      
      // Redirect to the newly created post
      if (response && response.id) {
        navigate(`/help-post/${response.id}`);
      }
    } catch (error) {
      message.error('Failed to create post');
    }
  };

  const handlePositionSelected = useCallback((newPosition) => {
    console.log("handlePositionSelected called with:", newPosition);
    setPosition(newPosition);
  }, []);

  const columns = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      render: (text) => text,
    },
    {
      title: '类型',
      dataIndex: 'post_type',
      key: 'post_type',
      filters: [
        { text: '猫咪丢失', value: 'lost' },
        { text: '发现猫咪', value: 'found' },
        { text: '领养', value: 'adoption' },
        { text: '救助', value: 'rescue' },
        { text: '其它', value: 'other' },
      ],
      onFilter: (value, record) => record.post_type === value,
      render: (post_type) => {
        const types = {
          'lost': '猫咪丢失',
          'found': '发现猫咪',
          'adoption': '领养',
          'rescue': '救助',
          'other': '其它'
        };
        return types[post_type] || post_type;
      },
    },
    {
      title: '用户',
      dataIndex: 'creator',
      key: 'creator',
      render: (creator) => creator ? `${creator.first_name} ${creator.last_name}`.trim() || creator.username : 'Unknown',
    },
    {
      title: '发布时间',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      render: (created_at) => new Date(created_at).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }),
    },
  ];

  const showModal = () => {
    if (!isUserLoggedIn) {
      message.info('请先登录');
      navigate('/login');
      return;
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setPosition(null);
  };

  const handleRowClick = (record) => {
    navigate(`/help-post/${record.id}`);
  };

  const handleRowMouseEnter = (record) => {
    setHoveredPostId(record.id);
  };

  const handleRowMouseLeave = () => {
    setHoveredPostId(null);
  };

  // Function to handle temporary city selection
  const handleTempCitySelect = (city) => {
    setTempSelectedCity(city);
    setTempSelectedDistrict(null);
  };

  // Function to handle temporary district selection
  const handleTempDistrictSelect = (district) => {
    setTempSelectedDistrict(district);
  };
  
  // Function to handle confirmation button click
  const handleConfirmLocation = async () => {
    console.log("Confirm location clicked with:", {
      province: tempSelectedProvince,
      city: tempSelectedCity,
      district: tempSelectedDistrict
    });
    
    // Update the actual selected values
    setSelectedProvince(tempSelectedProvince);
    setSelectedCity(tempSelectedCity);
    setSelectedDistrict(tempSelectedDistrict);
    
    // Find coordinates for the selected location
    let latitude = null;
    let longitude = null;
    
    // Try to get coordinates from district first
    if (tempSelectedDistrict) {
      console.log("Looking for district coordinates:", tempSelectedDistrict);
      const districtData = regionsData.find(item => 
        item.province === tempSelectedProvince && 
        item.city === tempSelectedCity && 
        item.district === tempSelectedDistrict
      );
      
      if (districtData && districtData.lat && districtData.lng) {
        latitude = districtData.lat;
        longitude = districtData.lng;
        console.log("Found district coordinates:", latitude, longitude);
      }
    }
    
    // If no district coordinates, try city
    if (!latitude && !longitude && tempSelectedCity) {
      console.log("Looking for city coordinates:", tempSelectedCity);
      const cityData = regionsData.find(item => 
        item.province === tempSelectedProvince && 
        item.city === tempSelectedCity && 
        (!item.district || item.district === '')
      );
      
      if (cityData && cityData.lat && cityData.lng) {
        latitude = cityData.lat;
        longitude = cityData.lng;
        console.log("Found city coordinates:", latitude, longitude);
      } else {
        // Try to find any entry with this city
        const anyCityData = regionsData.find(item => 
          item.province === tempSelectedProvince && 
          item.city === tempSelectedCity
        );
        
        if (anyCityData && anyCityData.lat && anyCityData.lng) {
          latitude = anyCityData.lat;
          longitude = anyCityData.lng;
          console.log("Found any city coordinates:", latitude, longitude);
        }
      }
    }
    
    // If no city coordinates, try province
    if (!latitude && !longitude && tempSelectedProvince) {
      console.log("Looking for province coordinates:", tempSelectedProvince);
      const provinceData = regionsData.find(item => 
        item.province === tempSelectedProvince && 
        (!item.city || item.city === '')
      );
      
      if (provinceData && provinceData.lat && provinceData.lng) {
        latitude = provinceData.lat;
        longitude = provinceData.lng;
        console.log("Found province coordinates:", latitude, longitude);
      } else {
        // Try to find any entry with this province
        const anyProvinceData = regionsData.find(item => 
          item.province === tempSelectedProvince
        );
        
        if (anyProvinceData && anyProvinceData.lat && anyProvinceData.lng) {
          latitude = anyProvinceData.lat;
          longitude = anyProvinceData.lng;
          console.log("Found any province coordinates:", latitude, longitude);
        }
      }
    }
    
    // Center the map if coordinates were found
    if (latitude && longitude) {
      console.log("Centering map to:", latitude, longitude);
      if (mapRef.current) {
        try {
          mapRef.current.centerMap(latitude, longitude);
          console.log("Map centered successfully");
        } catch (error) {
          console.error("Error centering map:", error);
        }
      } else {
        console.error("Map reference is not available");
      }
    } else {
      console.error("Could not find coordinates for the selected location");
    }
    
    // Fetch posts that match the selected location
    try {
      setLoading(true);
      let url = '/api/get-posts/';
      const params = new URLSearchParams();
      
      if (tempSelectedProvince) {
        params.append('province', tempSelectedProvince);
      }
      if (tempSelectedCity) {
        params.append('city', tempSelectedCity);
      }
      if (tempSelectedDistrict) {
        params.append('district', tempSelectedDistrict);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const data = await get(url);
      setPosts(data);
      setLoading(false);
    } catch (error) {
      message.error('Failed to fetch filtered posts');
      setLoading(false);
    }
  };

  // Function to handle user location button click
  const handleMyLocationClick = () => {
    if (navigator.geolocation && mapRef.current) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          mapRef.current.centerMap(latitude, longitude);
        },
        (error) => {
          message.error('无法获取您的位置: ' + error.message);
        }
      );
    } else {
      message.error('您的浏览器不支持地理位置功能');
    }
  };

  return (
    <div className="help-post-page">
      <div className="help-post-container">
        <div className="help-post-header">
          <h2 className="help-post-title">互助地图</h2>
          <div className="help-post-count">
            <InfoCircleOutlined className="count-icon" />
            在此查看您社区附近的猫咪救助信息，当前区域共有 {posts.length} 个互助贴
          </div>
          <Button type="primary" onClick={showModal} className="help-post-button">
              <PostAddIcon className="button-icon" />
              发帖互助
          </Button>
        </div>

          <div className="location-controls">
            <Space size="small" style={{ padding: '12px', background: '#f9f9f9', borderRadius: '8px', marginBottom: '16px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
              <Select
                placeholder="选择省份"
                style={{ width: 120 }}
                value={tempSelectedProvince}
                onChange={setTempSelectedProvince}
                size="middle"
              >
                {provinces.map(province => (
                  <Option key={province.value} value={province.value}>{province.label}</Option>
                ))}
              </Select>
              
              <Select
                placeholder="选择城市"
                style={{ width: 120 }}
                value={tempSelectedCity}
                onChange={handleTempCitySelect}
                disabled={!tempSelectedProvince}
                size="middle"
              >
                {cities.map(city => (
                  <Option key={city.value} value={city.value}>{city.label}</Option>
                ))}
              </Select>
              
              <Select
                placeholder="选择区县"
                style={{ width: 120 }}
                value={tempSelectedDistrict}
                onChange={handleTempDistrictSelect}
                disabled={!tempSelectedCity || districts.length === 0}
                size="middle"
              >
                {districts.map(district => (
                  <Option key={district.value} value={district.value}>{district.label}</Option>
                ))}
              </Select>
              
              <Button 
                type="primary"
                onClick={handleConfirmLocation}
                size="middle"
                style={{ marginLeft: '8px' }}
              >
                确定
              </Button>
              
              <Button 
                icon={<AimOutlined />} 
                onClick={handleMyLocationClick}
                className="my-location-button"
                size="middle"
              >
                我的位置
              </Button>
            </Space>
          </div>
        
        <Table
          className="help-post-table"
          dataSource={posts}
          columns={columns}
          rowKey="id"
          pagination={{ 
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '50'],
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`
          }}
          size="small"
          loading={loading}
          locale={{ 
            emptyText: selectedProvince || selectedCity || selectedDistrict ? 
              `当前选择的位置 ${selectedProvince || ''}${selectedCity ? ' ' + selectedCity : ''}${selectedDistrict ? ' ' + selectedDistrict : ''} 没有互助信息` : 
              '暂无互助信息' 
          }}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            onMouseEnter: () => handleRowMouseEnter(record),
            onMouseLeave: () => handleRowMouseLeave(),
            style: { cursor: 'pointer' }
          })}
          scroll={{ y: 'calc(100vh - 280px)' }}
          bordered={false}
        />

        <Modal
          title="发帖互助"
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          width={1000}
          className="help-post-modal"
        >
          <div className="help-post-modal-content">
            <div className="help-post-map-section">
              <CreatePostMap 
                ref={createPostMapRef}
                onPositionSelected={handlePositionSelected} 
              />
            </div>
            <div className="help-post-form-section">
              <Form form={form} onFinish={onFinish} layout="vertical" className="help-post-form">
                <Form.Item
                  name="post_type"
                  label="帖子类型"
                  rules={[{ required: true, message: '请选择帖子类型' }]}
                >
                  <Select placeholder="选择帖子类型">
                    <Option value="lost">猫咪丢失</Option>
                    <Option value="found">发现猫咪</Option>
                    <Option value="adoption">领养</Option>
                    <Option value="rescue">救助</Option>
                    <Option value="other">其它</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="title"
                  label="标题"
                  rules={[{ required: true, message: '请输入标题' }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="description"
                  label="描述"
                  rules={[{ required: true, message: '请输入描述' }]}
                >
                  <Input.TextArea />
                </Form.Item>
                <div className="location-fields">
                  <Form.Item
                    name="province"
                    label="省份"
                    rules={[{ required: true, message: '请选择省份' }]}
                    style={{ display: 'inline-block', width: 'calc(33% - 8px)', marginRight: '8px' }}
                  >
                    <Select 
                      placeholder="选择省份"
                      onChange={handleProvinceSelectInModal}
                    >
                      {provinces.map(province => (
                        <Option key={province.value} value={province.value}>{province.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name="city"
                    label="城市"
                    rules={[{ required: false }]}
                    style={{ display: 'inline-block', width: 'calc(33% - 8px)', marginRight: '8px' }}
                  >
                    <Select 
                      placeholder="选择城市"
                      onChange={handleCitySelectInModal}
                      disabled={!selectedProvince}
                      allowClear
                    >
                      {cities.map(city => (
                        <Option key={city.value} value={city.value}>{city.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name="district"
                    label="区县"
                    rules={[{ required: false }]}
                    style={{ display: 'inline-block', width: 'calc(33% - 8px)' }}
                  >
                    <Select 
                      placeholder="选择区县"
                      onChange={handleDistrictSelectInModal}
                      disabled={!selectedCity || districts.length === 0}
                      allowClear
                    >
                      {districts.map(district => (
                        <Option key={district.value} value={district.value}>{district.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
                <Form.Item
                  name="cat"
                  label="选择猫咪"
                  rules={[{ required: false, message: '请选择一只猫咪' }]}
                >
                  <Select placeholder="选择一只猫咪">
                    {cats.map(cat => (
                      <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit">提交</Button>
                </Form.Item>
              </Form>
            </div>
          </div>
        </Modal>
      </div>
      <div className="help-post-map-container">
        <HelpPostMap 
          ref={mapRef}
          helpPosts={posts} 
          hoveredPostId={hoveredPostId} 
        />
      </div>
    </div>
  );
};

export default HelpPost;
